import React from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import {Field, reduxForm, formValueSelector, initialize} from 'redux-form'
import FormInput from 'components/FormInput'
import FormSelect from 'components/FormSelect'
import Loader from 'components/Loader'
import VectorImage from 'components/VectorImage'
import Submit from './Submit'
import validate from './validate'
import partial from 'utils/partial'
import classnames from 'classnames'
import moment from 'moment-timezone'
import sortBy from 'lodash/sortBy'
import gtmImage from './images/logos/logo_googletagmanager.svg'
import tz from 'utils/tz'
import currencies from 'utils/currencies'

const now = moment.utc()
const timezoneList = sortBy(tz.map(item => {
  const date = moment.tz(now, item)
  const offset = date.format('Z')
  return {name: `(GMT ${offset}) ${item}`, value: item, offset}
}), ['offset'])

const industryList = [
  'Auto and Moto',
  'Beauty',
  'Business and Finance',
  'Children and Maternity',
  'Design and Art',
  'Electronic and Tech',
  'Food and Drink',
  'Hobbies and Leisure',
  'Home and Garden',
  'Industrial and Special Equipment',
  'Internet and Communication',
  'Jobs and Education',
  'Law and Government',
  'Medicine and Healthcare',
  'News and Blogs',
  'Pets and Animals',
  'Post and Logistics',
  'Real Estate',
  'Shopping and Fashion',
  'Sport',
  'Travel',
  'Сonstruction',
  'Other'
].map(item => ({
  name: item,
  value: item
}))

const platforms = [
  {image: gtmImage, value: 'gtm'},
  {label: 'Other/custom platform', value: 'other'}
]

const visibleCurrencies = currencies.filter(c => !c.other)
const selectableCurrencies = currencies.filter(c => c.other).map(c => ({
  name: `${c.value} - ${c.name}`,
  value: c.value
}))

export class SiteCreation extends React.Component {
  static propTypes = {
    handleSubmit: PropTypes.func.isRequired,
    invalid: PropTypes.bool,
    submitting: PropTypes.bool,
    selectedPlatform: PropTypes.string,
    selectedCurrency: PropTypes.string,
    submitButtonText: PropTypes.string,
    change: PropTypes.func.isRequired,
    blur: PropTypes.func.isRequired,
    formValues: PropTypes.object,
    site: PropTypes.object,
    initialize: PropTypes.func,
    isBusy: PropTypes.bool,
    query: PropTypes.object,
    disabledFields: PropTypes.array,
    onSubmit: PropTypes.func
  }

  constructor(props) {
    super(props)
    this.state = {
      manual: false
    }
  }

  componentWillReceiveProps(nextProps) {
    const {formValues, initialize, site, query, onSubmit, isBusy, initialized} = nextProps

    if (!initialized) {
      if (isBusy) return false

      const queryValues = query ? {
        ...query,
        name: query.url
      } : {}

      const valid = !Object.keys(validate(queryValues)).length

      if (site) {
        initialize(site)
      } else if (query && valid) {
        initialize(queryValues)
        onSubmit(queryValues)
      } else if (formValues && formValues.wizard) {
        initialize(formValues.wizard)
      }
    }
  }

  render() {
    const {handleSubmit, isBusy, disabledFields} = this.props
    const classNames = classnames('wizard-step', {
      'is-busy': isBusy
    })
    const platformClassNames = classnames('wizard-step_platforms', {
      disabled: disabledFields.includes('platform')
    })
    return (
      <form className={classNames} onSubmit={handleSubmit}>
        {isBusy ? <Loader /> : null}
        <div className={platformClassNames}>
          {platforms.map(this.renderPlatform)}
        </div>
        {this.renderOtherFields()}
      </form>
    )
  }

  renderOtherFields() {
    const {invalid, submitting, selectedCurrency, submitButtonText, disabledFields} = this.props
    const currencyClassNames = classnames('auth-view_form-field', 'wizard-step_select', 'is-currency', {
      'is-selected': selectableCurrencies.find(c => c.value === selectedCurrency)
    })

    const urlDisabled = disabledFields.includes('url')
    const urlClassNames = classnames('wizard-step_block', {
      'is-disabled': urlDisabled
    })

    return (
      <div>
        <Field
          name='platform'
          type='hidden'
          component={FormInput}
        />
        <div className={urlClassNames}>
          <div className='wizard-step_title'>Your merchant domain</div>
          <Field
            className='wizard-step_input-field'
            name='url'
            placeholder='https://mydomain.com'
            disabled={urlDisabled || submitting}
            component={FormInput}
          />
        </div>
        <div className='wizard-step_title'>Account currency</div>
        <div className='wizard-step_currencies'>
          {visibleCurrencies.map(this.renderCurrency)}
          <Field
            className={currencyClassNames}
            name='currency'
            searchable
            disabled={submitting}
            component={FormSelect}
            emptyValue='Other'
            options={selectableCurrencies}
          />
        </div>
        <Field name='currency' type='hidden' component={FormInput} />
        <div className='wizard-step_title'>Industry</div>
        <Field
          className='auth-view_form-field wizard-step_select wizard-step_select_tz'
          name='industry'
          searchable
          disabled={submitting}
          component={FormSelect}
          emptyValue='Select Industry'
          options={industryList}
        />
        <div className='wizard-step_title'>Timezone</div>
        <Field
          className='auth-view_form-field wizard-step_select wizard-step_select_tz'
          name='timezone'
          searchable
          disabled={submitting}
          component={FormSelect}
          emptyValue='Select Timezone'
          options={timezoneList}
        />
        <p className='wizard-step_text'>
          When continuing you will receive an email with your personal installation instructions.
          You can also find them in Step 4 below.
        </p>
        <Field name='id' type='hidden' component={FormInput} />
        <Submit disabled={invalid || submitting}>{submitButtonText}</Submit>
      </div>
    )
  }

  renderPlatform = (platform, index) => {
    const {selectedPlatform, disabledFields} = this.props
    const {image, label, value} = platform
    const disabled = disabledFields.includes('platform')
    const classNames = classnames('wizard-step_platform', 'wizard-card', `is-${value}`, {
      'is-selected': selectedPlatform === value,
      'is-disabled': disabled
    })

    return (
      <div
        className={classNames}
        key={index}
        onClick={disabled ? null : partial(this.onPlatformClick, value)}
      >
        {
          value !== 'other'
            ? <VectorImage width='70%' source={image} />
            : null
        }
        {label && <span>{label}</span>}
      </div>
    )
  }

  renderCurrency = (currency, index) => {
    const {selectedCurrency} = this.props
    const {name, value} = currency
    const classNames = classnames('wizard-step_currency', 'wizard-card', `is-${value}`, {
      'is-selected': selectedCurrency === value
    })

    return (
      <div
        className={classNames}
        key={index}
        onClick={partial(this.onCountryClick, value)}
      >
        <div>
          <span>{name}</span>
        </div>
      </div>
    )
  }

  onTurnOnManual = () => {
    this.setState({manual: true})
  }

  onCountryClick = value => {
    this.props.change('currency', value)
  }

  onPlatformClick = value => {
    this.props.change('platform', value)
    this.props.blur('platform', value)
  }
}

const selector = formValueSelector('wizard')
const form = reduxForm({
  form: 'wizard',
  validate
})(SiteCreation)

export default connect(state => ({
  selectedPlatform: selector(state, 'platform'),
  selectedCurrency: selector(state, 'currency'),
  initialize
}))(form)



// WEBPACK FOOTER //
// ./client/components/Wizard/SiteCreation.js